import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { Sparkles, X, ArrowUp, Square, RotateCcw, Paperclip, FileText, AlertTriangle, History, Loader2, Inbox } from 'lucide-react'
import { MessageList } from './MessageList'
import { BuilderChatHistory } from './BuilderChatHistory'
import { useBuilderChatStream } from './useBuilderChatStream'
import { aggregateUsage, formatSessionHeadline, formatSessionDetail } from './format'
import { loadBuilderHistory, saveBuilderConversation, deleteBuilderConversation } from '../../../utils/aiHistoryDb'
import { ACCEPT_ATTR, isAccepted, openableAttachments, readFileAsAttachment } from './attachments'
import { AttachmentViewer } from './AttachmentViewer'
import { BLOCK_TARGET_EVENT } from './blockContext'
import { publishTurnStatus } from './turnStatus'
import { fetchBlockUsage, usageWarning } from './blockUsage'
import { fetchSidecarHealth, modelLabel, staleSidecarWarning } from './sidecarHealth'
import { isClaimedByAnotherTab, claim, release } from './conversationClaim'
import { fetchChatRuns, markRunsRead, mergeRuns, unreadElsewhereRuns, isLive, originHref, backgroundRuns, finishedHereRuns, newlyFinished, CHAT_PARAM } from './chatRuns'
import { claimPendingPlacements } from '../blockPlacement'
import { useOptionalAuth } from '../../../context/AuthContext';
import { CornerDockSlot } from '../../shell/CornerDock';
import { usePanneauDroit } from '../../shell/cornerDock';
import { DOCK_ORDER } from '../../shell/cornerDock';

const REGISTRY_INVALIDATE_EVENT = 'builder:registry:invalidate'

// Le cousin SANS effet de bord de l'événement ci-dessus : « relis le registre »,
// rien d'autre.
//
// Plus aucun émetteur ici : il existait parce qu'une invalidation d'arrière-plan
// aurait volé le placement d'un autre tour, du temps où il n'y en avait qu'un
// pour toute la page. Depuis qu'une place réservée appartient à SA conversation
// (cf. blockPlacement), la fin d'un tour d'arrière-plan peut — et doit — être
// annoncée comme une vraie fin : sans quoi le bloc qu'il a produit n'atterrit
// nulle part. Le canevas continue de l'écouter, pour un futur appelant.
const REGISTRY_REFRESH_EVENT = 'builder:registry:refresh'

// Cadences du sondage de `/api/builder/chat/runs`. Cet endpoint passe par le
// pool PHP-FPM `chat` (12 workers pour TOUS les éditeurs) : la requête est
// courte, mais elle en occupe un. Panneau fermé on espace — un tour d'arrière-plan
// dure des dizaines de secondes, vingt de retard sur sa fin ne se voient pas.
const POLL_OPEN_MS = 10000
const POLL_CLOSED_MS = 30000
// Un sondage qui n'aboutit pas doit rendre la main : sans borne, une requête
// coincée derrière un pool plein attendrait le `fastcgi_read_timeout`, soit une
// demi-heure.
const POLL_TIMEOUT_MS = 8000
// Combien de temps un tour lancé d'ici reste annoncé sur la seule foi du
// navigateur, avant que le serveur ne le confirme. Couvre l'écart entre l'envoi
// et le sondage suivant ; passé ce délai sans que `/chat/runs` en ait jamais
// parlé, c'est que la requête n'a pas abouti — et un voile qui ne s'éteint
// jamais est pire que pas de voile du tout.
const LOCAL_BRIDGE_MS = 60000

// Largeur de la COLONNE. Le panneau ne recouvre plus le canevas : il lui prend
// de la place (`pousse: true` → `--orbit-panneau-droit`, cf. shell/cornerDock.js
// et la règle `#orbit_front_app` d'index.css). Un panneau qui recouvre pouvait
// être large sans conséquence ; une colonne se règle, parce que l'arbitrage
// entre lire l'assistant et voir la page appartient à l'éditeur.
const PANEL_MIN_WIDTH = 320
const PANEL_MAX_WIDTH = 900
const PANEL_DEFAULT_WIDTH = 440
// Ce qu'on laisse au canevas quoi qu'il arrive : une colonne qui l'étrangle
// jusqu'à le rendre illisible n'aide personne, et sur un portable la borne haute
// seule ne suffit pas à l'empêcher.
const CANVAS_MIN_WIDTH = 480
// Pas du réglage au clavier (× 4 avec Maj) — la poignée est un `separator`
// focalisable, pas un ornement réservé à la souris.
const RESIZE_STEP = 16
const WIDTH_KEY = 'orbit.builderChat.width'

/** Largeur permise ici et maintenant : les bornes, puis ce que l'écran autorise. */
const clampWidth = (px) => {
  const viewport = typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth
  const max = Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, viewport - CANVAS_MIN_WIDTH))
  return Math.round(Math.min(Math.max(Number(px) || PANEL_DEFAULT_WIDTH, PANEL_MIN_WIDTH), max))
}

// Confort par navigateur, pas un réglage à retrouver ailleurs : `localStorage`
// convient, et il peut jeter (navigation privée, stockage bloqué) — d'où les
// deux gardes.
const readStoredWidth = () => {
  try { return clampWidth(Number(localStorage.getItem(WIDTH_KEY)) || PANEL_DEFAULT_WIDTH) }
  catch { return PANEL_DEFAULT_WIDTH }
}
const storeWidth = (px) => {
  try { localStorage.setItem(WIDTH_KEY, String(px)) }
  catch { /* stockage refusé : la largeur vaut pour cette session */ }
}

// Referenced by the composer's aria-describedby when a warning is showing.
const CHIP_WARNING_ID = 'builder-chat-target-warning'
const FILE_WRITE_HINT_ID = 'builder-chat-file-write-hint'

// Referenced by the history toggle's aria-controls.
const HISTORY_PANEL_ID = 'builder-chat-history'

/**
 * Le signalement au clavier et au lecteur d'écran.
 *
 * TOUJOURS dans le DOM : un lecteur d'écran n'annonce de façon fiable qu'un
 * changement DANS une région déjà présente — une région qui apparaît avec son
 * texte passe souvent inaperçue. Le texte vient d'un sondage, jamais d'un geste
 * de l'éditeur : `role="status"`.
 */
function BackgroundNotice({ text }) {
  return <p className="sr-only" role="status" aria-label="Réponses d’arrière-plan">{text ?? ''}</p>
}

/**
 * Dock-right chat panel for the page builder.
 *
 * Mounted by BuilderCanvas. When the agent's turn ENDS after a successful
 * create/update tool, a global `builder:registry:invalidate` event fires,
 * carrying `detail.types` — the block types touched during that turn. The
 * canvas listens, refetches THOSE defs only, reloads the blocks built from them
 * and adds a missing one at the end of the page.
 */
export function BuilderChatPanel({ host, onDashboardInvalidate } = {}) {
  const [open, setOpen] = useState(false)
  const [width, setWidth] = useState(readStoredWidth)

  // Deux effets d'une seule déclaration : la barre d'actions du coin s'écarte
  // (sinon ce panneau recouvre « Signaler »), et l'application RÉSERVE la
  // largeur — c'est ce qui fait la différence entre recouvrir et prendre place.
  usePanneauDroit('builder-chat', open, width, { pousse: true })

  // L'écran a changé de taille (fenêtre redimensionnée, rotation, second moniteur
  // débranché) : une largeur qui était permise peut ne plus laisser de canevas.
  useEffect(() => {
    const onResize = () => setWidth(w => clampWidth(w))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Écrit APRÈS le geste, pas pendant : un glissement produit des dizaines de
  // valeurs, et `localStorage` est synchrone.
  const widthRef = useRef(width)
  widthRef.current = width
  useEffect(() => {
    const timer = setTimeout(() => storeWidth(width), 300)
    return () => clearTimeout(timer)
  }, [width])

  // Le nettoyage du délai ci-dessus n'écrit RIEN : un réglage suivi d'une
  // navigation (ou d'une sortie du mode édition) dans les 300 ms était donc
  // perdu, et le panneau rouvrait à la largeur d'avant. Mesuré à l'écran.
  useEffect(() => {
    const flush = () => storeWidth(widthRef.current)
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [])
  const [draft, setDraft] = useState('')
  const [attachments, setAttachments] = useState([])
  // La pièce jointe EN COURS DE RÉDACTION affichée en grand, s'il y en a une.
  const [openAttachmentId, setOpenAttachmentId] = useState(null)
  const [attachError, setAttachError] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  // The block pinned by a canvas block's "modify with AI" button. Pinned means
  // it rides on EVERY message until the editor removes it, and it is what binds
  // the agent to that one block server-side.
  const [target, setTarget] = useState(null)
  const [usage, setUsage] = useState(null)
  // What the running agent service understands. A container that was not rebuilt
  // drops `blockContext` in silence, which would make the chip a lie.
  const [health, setHealth] = useState(null)
  // Le consentement d'écrire des fichiers. NI localStorage NI préférence de
  // compte : il retombe à chaque conversation neuve, pour que cocher reste un
  // geste contemporain de ce qu'on demande, et non un réglage oublié.
  const [allowFileWrite, setAllowFileWrite] = useState(false)
  // `useOptionalAuth` et non `useAuth` : ce fichier est importé depuis
  // BuilderCanvas, qui rend aussi hors AuthProvider (page publique, SSG).
  const auth = useOptionalAuth()
  // Le DROIT vient de Drupal, et il est revérifié par le proxy à chaque tour :
  // ceci ne fait que masquer une case qui ne servirait à rien. La capacité doit
  // aussi exister sur ce déploiement — en prod le sidecar ne l'annonce pas.
  const canOfferFileWrite = Boolean(auth?.hasPermission('use builder ai file writes'))
    && Boolean(health?.features?.includes('fileWrite'))
  // Stored conversations, and whether the picker is showing instead of the
  // transcript. Loaded on demand rather than kept in sync: the persist effect
  // below writes on every message, a live list would just be churn.
  const [historyEntries, setHistoryEntries] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const textareaRef = useRef(null)
  const fileInputRef = useRef(null)
  // Drag enter/leave fire for every child element; a depth counter keeps the
  // overlay from flickering as the cursor moves over the panel's contents.
  const dragDepth = useRef(0)

  // Les conversations dont la fin a DÉJÀ été annoncée. Un tour suivi jusqu'au
  // bout est annoncé par le flux ; le sondage le voit finir juste après (le
  // temps que `isStreaming` retombe, il n'est plus « celui qu'on regarde ») et
  // l'annoncerait une seconde fois — le canevas poserait alors un deuxième
  // exemplaire du bloc, en bas de page, faute de place réservée encore due.
  const announcedRef = useRef(new Set())

  const fireInvalidation = useCallback((detail) => {
    if (detail?.conversationId) announcedRef.current.add(detail.conversationId)
    window.dispatchEvent(new CustomEvent(REGISTRY_INVALIDATE_EVENT, { detail }))
  }, [])

  const { messages, isStreaming, isReconnecting, queuePosition, error, send, stop, detach, reset, resumeConversation, resumeRun, getPendingTurn, conversationId, sessionId, origin } = useBuilderChatStream({
    onRegistryInvalidate: fireInvalidation,
    onDashboardInvalidate,
    host,
  })

  // Reopen the latest conversation on first open, and keep the rest for the
  // history picker (see `toggleHistory` below).
  //
  // Restoring the messages is not enough: `sessionId` is what the agent
  // resumes from, so without it a reload leaves the transcript on screen but
  // the agent answering with no idea what is displayed above it.
  const loadedRef = useRef(false)
  useEffect(() => {
    if (!open || loadedRef.current) return
    loadedRef.current = true
    loadBuilderHistory().then(entries => {
      setHistoryEntries(entries)
      const latest = entries[0]
      // Une conversation tenue par un AUTRE onglet ne doit pas être adoptée ici :
      // les deux écriraient sous la même clé, et le second verrait la réponse du
      // premier. Dans ce cas on ouvre une conversation neuve — ce que l'éditeur
      // veut de toute façon en ouvrant un second onglet.
      if (latest?.messages?.length && !isClaimedByAnotherTab(latest.id)) {
        resumeConversation({
          conversationId: latest.id,
          sessionId: latest.sessionId,
          messages: latest.messages,
          origin: latest.origin,
        })
        // Le bloc épinglé fait partie de la conversation, pas de la session de
        // navigation : sans lui, la question suivante repart sans contexte et
        // l'assistant CRÉE un bloc au lieu de modifier celui qu'on regardait.
        setTarget(latest.target ?? null)
        setUsage(null)
        // Le tour n'avait pas fini quand la page est partie : le run, lui, a
        // continué dans le sidecar. On s'y rebranche là où le transcript
        // s'arrête. Sans ça, un simple F5 en plein tour perdait la réponse —
        // rien ne s'affichait alors que le processus tournait toujours.
        if (latest.pending) {
          resumeRun({
            conversationId: latest.id,
            lastId: latest.pending.lastId ?? null,
            context: latest.pending.context,
          })
        }
      }
    }).catch(() => { /* ignore — first run / private mode */ })
  }, [open, resumeConversation, resumeRun])

  // Ce qu'il faut écrire, lu à l'INSTANT de l'écriture. `flushConversation` sert
  // aussi de nettoyage de démontage : son identité doit donc être stable, et elle
  // ne peut pas fermer sur `messages`.
  const snapshotRef = useRef(null)
  snapshotRef.current = { conversationId, sessionId, origin, target, messages, getPendingTurn }

  // Persist on every message change. Title = first user message (truncated).
  // `sessionId` rides along so re-opening the panel resumes the agent's own
  // session rather than starting a fresh one. The store has `keyPath: 'id'`
  // and no field schema, so this needs no DB version bump.
  //
  // Extraite de son effet pour être appelable À LA DEMANDE : depuis qu'on peut
  // quitter une conversation dont le tour CONTINUE, il faut écrire le point de
  // reprise juste avant de lâcher le flux — pas seulement au fil des messages.
  const flushConversation = useCallback(() => {
    const snap = snapshotRef.current
    if (!snap || snap.messages.length === 0) return
    const firstUser = snap.messages.find(m => m.role === 'user')
    const title = firstUser ? firstUser.text.slice(0, 60) : 'Conversation'
    saveBuilderConversation({
      id: snap.conversationId,
      title,
      sessionId: snap.sessionId,
      messages: snap.messages,
      // L'écran d'où la conversation est partie. Le store n'a ni schéma de
      // champs ni version à incrémenter — une entrée antérieure le rend
      // simplement `undefined`, et la ligne s'affiche alors sans adresse.
      origin: snap.origin,
      // Le bloc épinglé, enregistré AVEC la conversation : c'en est une partie,
      // au même titre que le transcript. Il est renvoyé à chaque tour (cf.
      // `send`), donc une conversation rouverte sans lui perdrait le contexte
      // dont dépendent les questions qui la composent.
      target: snap.target,
      // Où en est le tour EN COURS, s'il y en a un : c'est ce qui permet de le
      // rejoindre après un rechargement, au lieu de restaurer un transcript figé
      // pendant que le run continue tout seul. `null` une fois le tour terminé.
      pending: snap.getPendingTurn(),
      updatedAt: Date.now(),
    })
  }, [])

  // `isStreaming` est dans les dépendances pour une raison précise : le dernier
  // changement de `messages` d'un tour est l'événement `result`, qui précède la
  // trame terminale. Sans cette dépendance, l'effet ne re-tournerait jamais
  // après la fin du tour et `pending` resterait armé pour toujours — la
  // réouverture suivante irait se rebrancher sur un run déjà fini.
  useEffect(() => {
    flushConversation()
  }, [messages, conversationId, sessionId, origin, target, isStreaming, flushConversation])

  // Le nettoyage éteint les voiles au démontage du panneau : les tours, eux,
  // continuent côté sidecar, mais plus personne ici ne saura dire quand ils
  // finissent — un voile laissé allumé ne s'éteindrait jamais. (La publication
  // elle-même est plus bas : elle a besoin de la liste sondée.)
  useEffect(() => () => publishTurnStatus([]), [])

  // Démontage : navigation react-router, sortie du mode édition, ou retour
  // anticipé `key="builder-canvas-loading"` de BuilderCanvas. Le tour, lui,
  // CONTINUE côté sidecar — c'est tout l'intérêt — donc il faut laisser derrière
  // soi de quoi le retrouver. Pas de `detach()` ici : il ferait des `setState`
  // sur un composant démonté, et le nettoyage du hook avorte déjà le flux.
  useEffect(() => () => flushConversation(), [flushConversation])

  // Fermeture d'onglet ou rechargement dur : le nettoyage de démontage ne tourne
  // pas. `pagehide` plutôt que `beforeunload` — il couvre aussi le bfcache et
  // n'affiche aucun dialogue.
  useEffect(() => {
    const onHide = () => flushConversation()
    window.addEventListener('pagehide', onHide)
    return () => window.removeEventListener('pagehide', onHide)
  }, [flushConversation])

  // Où en était le flux quand on l'a lâché. `getPendingTurn()` rend `null` une
  // fois détaché : le curseur doit donc être capté AVANT. Il sert à se rebrancher
  // à la réouverture du panneau, où l'on n'a pas relu IndexedDB.
  const detachedCursorRef = useRef(null)

  /**
   * Lâcher le flux de la conversation courante — sans abandonner son tour.
   *
   * L'ordre porte le sens : capter le curseur, écrire, PUIS détacher.
   */
  const detachStream = useCallback(() => {
    const pending = getPendingTurn()
    detachedCursorRef.current = pending
      ? { conversationId, lastId: pending.lastId, context: pending.context }
      : null
    flushConversation()
    detach()
  }, [conversationId, detach, flushConversation, getPendingTurn])

  // Tant que le panneau est ouvert, cet onglet DÉTIENT sa conversation : les
  // autres onglets ne la reprendront pas à leur ouverture. Rafraîchi
  // périodiquement pour que la revendication ne périme pas sous un onglet vivant,
  // et relâchée à la fermeture — un onglet fermé net laisse simplement expirer.
  useEffect(() => {
    if (!open || !conversationId) return
    claim(conversationId)
    const timer = setInterval(() => claim(conversationId), 10000)
    return () => {
      clearInterval(timer)
      release(conversationId)
    }
  }, [open, conversationId])

  // Cumulative session cost + tokens. Re-derives from messages each render
  // so it survives reloads-from-IndexedDB and resets when the user clicks
  // "Nouvelle conversation" (because messages → []).
  const sessionStats = useMemo(() => aggregateUsage(messages), [messages])
  const sessionHeadline = formatSessionHeadline(sessionStats)
  const sessionTooltip = formatSessionDetail(sessionStats)

  // Le champ grandit avec la demande, jusqu'à une douzaine de lignes — au-delà
  // il défile, plutôt que de manger la conversation.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 72), 260)}px`
  }, [draft])

  // The canvas announces a block to edit (the ✨ on its toolbar): open the panel
  // and pin the context. NOTHING is sent — the editor writes the request.
  //
  // Chaque clic sur « Modifier avec l'IA » ouvre une conversation NEUVE : le
  // `reset()` jette l'id de session de l'agent (plus de resume côté sidecar) et
  // vide le transcript, pour que la demande parte d'un contexte propre — celui
  // du bloc qu'on vient d'épingler, pas la discussion précédente. Le
  // `loadedRef` est armé au passage : sans lui, l'effet « charger la dernière
  // conversation à la première ouverture » remettrait aussitôt l'ancienne
  // par-dessus. L'ancienne reste dans l'historique IndexedDB, sous son id.
  useEffect(() => {
    const onTarget = (e) => {
      loadedRef.current = true
      // Le tour en cours n'est pas abandonné : on le lâche, en gardant de quoi
      // le rejoindre. Avant, épingler un bloc pendant une réponse la tuait.
      detachStream()
      reset()
      setTarget(e.detail ?? null)
      setUsage(null)
      // Épingler un bloc, c'est vouloir écrire une demande — pas relire ses
      // conversations. L'historique laissé ouvert d'une consultation précédente
      // masquait le champ de saisie sur lequel le focus venait d'atterrir.
      setShowHistory(false)
      setOpen(true)
    }
    window.addEventListener(BLOCK_TARGET_EVENT, onTarget)
    return () => window.removeEventListener(BLOCK_TARGET_EVENT, onTarget)
  }, [detachStream, reset])

  // Focus the composer once the panel is mounted — a programmatic focus move
  // that follows an explicit user action (WCAG 3.2.1/3.2.2).
  useEffect(() => {
    if (target && open) textareaRef.current?.focus()
  }, [target, open])

  // "Is this type used elsewhere?" — the edit lands on the block DEFINITION, so
  // on every instance of it. Informative, never blocking: a failure just leaves
  // the chip without a warning.
  //
  // Keyed on the whole `target`, not `target.type`: pinning a DIFFERENT block of
  // the SAME type still resets `usage` above, so an effect keyed on the type
  // alone would never re-run and the warning would vanish for good.
  useEffect(() => {
    if (!target?.type) return
    const ac = new AbortController()
    fetchBlockUsage(target.type, ac.signal).then(setUsage)
    return () => ac.abort()
  }, [target])

  // Le consentement d'écrire des fichiers ne franchit AUCUNE frontière de
  // conversation. Un effet sur l'identifiant plutôt qu'un appel dans
  // `resetAll` : on change aussi de conversation en en rouvrant une depuis
  // l'historique et en épinglant un bloc, et un chemin ajouté demain hériterait
  // sinon d'un « oui » donné pour tout autre chose — sans rien à l'écran qui le
  // dise, puisque la case, elle, resterait cochée.
  useEffect(() => { setAllowFileWrite(false) }, [conversationId])

  // Asked once per panel opening — the answer only changes on a service restart.
  useEffect(() => {
    if (!open) return
    const ac = new AbortController()
    fetchSidecarHealth(ac.signal).then(setHealth)
    return () => ac.abort()
  }, [open])

  const unpinTarget = useCallback(() => {
    setTarget(null)
    setUsage(null)
    // The ✕ removes itself from the DOM; without this the focus falls back to
    // <body> and the keyboard user loses their place (WCAG 2.4.3).
    textareaRef.current?.focus()
  }, [])

  // New conversation = new context: the pinned target does not survive it.
  const resetAll = useCallback(() => {
    unpinTarget()
    detachStream()
    reset()
    // Même raison qu'à l'épinglage : on demande une conversation neuve, on doit
    // se retrouver devant elle.
    setShowHistory(false)
  }, [detachStream, reset, unpinTarget])

  // Fermer, c'est cesser de regarder — donc lâcher le flux et rendre le worker
  // PHP-FPM qu'il tenait (il n'y en a que douze pour tous les éditeurs). Le tour
  // continue, la pastille du bouton flottant le dit, et la réouverture s'y
  // rebranche.
  const closePanel = useCallback(() => {
    detachStream()
    setOpen(false)
  }, [detachStream])

  // La liste du SERVEUR : tours en cours (y compris d'un autre onglet ou d'un
  // tour qu'on a quitté) et réponses terminées pendant une absence. Sondée tant
  // que le composant est MONTÉ — panneau ouvert OU fermé —, parce que c'est elle
  // qui alimente la pastille du bouton flottant. Démonté, l'intervalle est
  // nettoyé : on ne sonde pas depuis un écran où l'assistant n'est pas joignable.
  const [serverRuns, setServerRuns] = useState([])
  const pollAbortRef = useRef(null)
  const pollBusyRef = useRef(false)

  const pollRuns = useCallback(async () => {
    // Garde d'in-flight : `setInterval` ne saute pas un tick parce que le
    // précédent n'est pas revenu. Le pool `chat` est `static` — une requête
    // bloquée ne se dégrade pas, elle s'empile —, donc sans cette garde un
    // sondage lent multiplierait les connexions au lieu de les espacer.
    if (pollBusyRef.current) return
    pollBusyRef.current = true
    const ac = new AbortController()
    pollAbortRef.current = ac
    const timer = setTimeout(() => ac.abort(), POLL_TIMEOUT_MS)
    try {
      const runs = await fetchChatRuns(ac.signal)
      // `fetchChatRuns` rend `[]` sur TOUTE erreur, abandon compris : l'écrire
      // effacerait la pastille sur un simple délai dépassé.
      if (!ac.signal.aborted) setServerRuns(runs)
    }
    finally {
      clearTimeout(timer)
      pollBusyRef.current = false
    }
  }, [])

  useEffect(() => {
    const tick = () => {
      // Onglet caché : personne ne regarde la pastille. Un onglet laissé ouvert
      // toute la journée ne coûte alors plus rien au pool, et le retour à
      // l'écran redemande tout de suite.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      pollRuns()
    }
    tick()
    const timer = setInterval(tick, open ? POLL_OPEN_MS : POLL_CLOSED_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') tick() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [open, pollRuns])

  // Le sondage en vol n'est abandonné qu'au DÉMONTAGE. Le couper à chaque
  // ouverture ou fermeture du panneau — cet effet-ci se rejoue sur `open` —
  // jetait sa réponse ET sautait le sondage immédiat qui suit, la garde
  // d'in-flight n'étant pas encore retombée : la liste des tours restait périmée
  // pour tout un intervalle, jusqu'à trente secondes panneau fermé. C'est
  // exactement le « ça disparaît quand je change d'écran » qu'on répare ici.
  useEffect(() => () => pollAbortRef.current?.abort(), [])

  // Le tour qu'on vient de finir doit sortir du compteur TOUT DE SUITE : sinon
  // on lit « 1 tour en cours » en regardant la réponse terminée sous ses yeux.
  const wasStreamingRef = useRef(false)
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) pollRuns()
    wasStreamingRef.current = isStreaming
  }, [isStreaming, pollRuns])

  // Les tours lancés d'ici que le sondage n'a pas encore vus. `/chat/runs` rend
  // un ÉTAT sondé toutes les dix secondes : sans ce relais, épingler un second
  // bloc dans la seconde qui suit un envoi éteindrait le voile du premier, et le
  // canevas mentirait jusqu'au sondage suivant. Chaque entrée s'efface dès que le
  // serveur connaît le tour — c'est lui qui sait quand il finit —, ou au bout de
  // LOCAL_BRIDGE_MS si la requête n'a jamais abouti.
  const [pendingLocal, setPendingLocal] = useState([])

  const prunePendingLocal = useCallback(() => {
    setPendingLocal((prev) => {
      if (prev.length === 0) return prev
      const cutoff = Date.now() - LOCAL_BRIDGE_MS
      const next = prev.filter(entry => (
        entry.at > cutoff && !serverRuns.some(run => run.conversationId === entry.conversationId)
      ))
      return next.length === prev.length ? prev : next
    })
  }, [serverRuns])

  // La purge doit être armée par le TEMPS autant que par la donnée : `serverRuns`
  // n'est pas réécrit quand un sondage expire (`POLL_TIMEOUT_MS`), donc un sidecar
  // durablement lent figerait cette liste — et le voile d'un bloc, qui l'empêche
  // d'être modifié ou déplacé, ne s'éteindrait jamais.
  useEffect(() => {
    prunePendingLocal()
    if (pendingLocal.length === 0) return
    const oldest = Math.min(...pendingLocal.map(e => e.at))
    const timer = setTimeout(prunePendingLocal, Math.max(1000, oldest + LOCAL_BRIDGE_MS - Date.now()))
    return () => clearTimeout(timer)
  }, [pendingLocal, prunePendingLocal])

  // L'envoi a échoué (quota refusé, sidecar injoignable, 4xx) : aucun tour ne
  // tourne, et l'erreur est déjà à l'écran. Garder le voile jusqu'au bout de la
  // borne verrouillerait le bloc une minute pour rien.
  useEffect(() => {
    if (!error) return
    setPendingLocal(prev => prev.filter(entry => entry.conversationId !== conversationId))
  }, [error, conversationId])

  const mergedHistory = useMemo(() => mergeRuns(historyEntries, serverRuns), [historyEntries, serverRuns])
  // Le bandeau du bas n'annonce plus que ce que cette page n'a PAS vu tourner :
  // le reste tient sa place en tête, là où l'éditeur le suivait.
  const unread = useMemo(() => unreadElsewhereRuns(serverRuns), [serverRuns])

  // Deux gestes distincts sur le bandeau des réponses non lues :
  // « Marquer comme lu » abaisse le drapeau CÔTÉ SERVEUR — l'annonce ne revient
  // plus, ici comme dans un autre navigateur ; « Masquer » ne fait taire que
  // cette session, et une réponse qui arrivera ensuite rallumera le bandeau.
  // Sans le premier, la seule façon d'éteindre l'annonce était de rouvrir la
  // conversation.
  const [dismissedUnread, setDismissedUnread] = useState([])
  const visibleUnread = useMemo(
    () => unread.filter(run => !dismissedUnread.includes(run.conversationId)),
    [unread, dismissedUnread],
  )

  const dismissUnread = useCallback(() => {
    setDismissedUnread(prev => [...new Set([...prev, ...unread.map(run => run.conversationId)])])
  }, [unread])

  // Marque ce que le bandeau ANNONCE — pas ce qui a déjà été masqué : le libellé
  // du bouton compte les mêmes réponses que celles qu'il éteint.
  const markUnreadRead = useCallback(async () => {
    const ids = visibleUnread.map(run => run.conversationId)
    if (ids.length === 0) return
    // Optimiste : le bandeau tombe au clic. En cas d'échec on le remet plutôt
    // que de laisser croire à une lecture qui n'a pas été enregistrée.
    setDismissedUnread(prev => [...new Set([...prev, ...ids])])
    const ok = await markRunsRead(ids)
    if (!ok) {
      setDismissedUnread(prev => prev.filter(id => !ids.includes(id)))
      return
    }
    // Relu tout de suite : les pastilles « non lue » de l'historique lisent la
    // même liste, et le sondage suivant est à dix secondes.
    fetchChatRuns().then(setServerRuns)
  }, [visibleUnread])

  // La conversation dont le flux est BRANCHÉ — pas celle qui est affichée.
  // Panneau fermé, on a détaché sans changer d'écran : le tour de la
  // conversation visible compte alors comme n'importe quel autre.
  const attachedId = isStreaming ? conversationId : null
  const background = useMemo(() => backgroundRuns(serverRuns, attachedId), [serverRuns, attachedId])
  const runningCount = useMemo(() => background.filter(run => run.status === 'running').length, [background])
  const queuedCount = background.length - runningCount

  // Les tours terminés que CETTE page a vus tourner et que l'éditeur n'a pas
  // encore ouverts. Ils gardent leur ligne en tête, à la place où il les
  // suivait, au lieu de redescendre dans le bandeau « pendant votre absence » :
  // il n'était pas absent. `readHere` les en retire dès qu'il les ouvre — le
  // drapeau du serveur, lui, ne retombe qu'au sondage suivant, et pas du tout
  // tant que le tour est encore dans le registre du sidecar.
  const [readHere, setReadHere] = useState([])
  const finishedHere = useMemo(
    () => finishedHereRuns(serverRuns, attachedId).filter(run => !readHere.includes(run.conversationId)),
    [serverRuns, attachedId, readHere],
  )
  // Une seule liste à l'écran : ce qui tourne, puis ce qui vient d'aboutir.
  const dockedRuns = useMemo(() => [...background, ...finishedHere], [background, finishedHere])

  // Un tour en file n'a pas commencé : l'annoncer « en cours » serait un
  // mensonge que l'historique dément deux clics plus loin (« En attente d'une
  // place (2e) »).
  const backgroundLabel = useMemo(() => {
    const parts = []
    if (runningCount) parts.push(`${runningCount} tour${runningCount > 1 ? 's' : ''} en cours`)
    if (queuedCount) parts.push(`${queuedCount} en attente`)
    return parts.join(', ')
  }, [runningCount, queuedCount])

  // Ce que le canevas doit voiler : TOUS les tours vivants, et plus seulement
  // celui qu'on regarde. Le tour attaché est publié depuis l'état local (donc
  // immédiat) ; les autres viennent du sondage, seul à les connaître encore une
  // fois la conversation quittée — d'où la cible que le sidecar renvoie
  // désormais avec chaque tour. Le relais `pendingLocal` couvre les secondes
  // entre l'envoi et le sondage suivant.
  //
  // Le RÉSULTAT, lui, continue de passer par `builder:registry:invalidate`.
  const liveTurns = useMemo(() => {
    const list = []
    // `watched` : ce tour-ci est celui dont l'écran tient le flux. C'est ce qui
    // autorise le canevas à lui réserver la place armée par son « + », sans la
    // rouvrir pour un tour d'un autre onglet (cf. `anyTurnMayPlace`).
    if (isStreaming) {
      list.push({ conversationId, target, status: queuePosition ? 'queued' : 'running', watched: true })
    }
    for (const run of background) {
      list.push({ conversationId: run.conversationId, target: run.target ?? null, status: run.status })
    }
    const known = new Set(list.map(t => t.conversationId))
    for (const local of pendingLocal) {
      if (!known.has(local.conversationId)) list.push({ ...local, status: 'running' })
    }
    return list
  }, [isStreaming, queuePosition, conversationId, target, background, pendingLocal])

  useEffect(() => { publishTurnStatus(liveTurns) }, [liveTurns])

  // Ce que le bouton flottant doit dire, panneau fermé : ce qui tourne ET ce qui
  // attend d'être lu. C'est le seul endroit où l'éditeur peut apprendre qu'il se
  // passe quelque chose sans rien ouvrir.
  const dockCount = dockedRuns.length + visibleUnread.length
  const dockLabel = useMemo(() => {
    const parts = []
    if (backgroundLabel) parts.push(backgroundLabel)
    // Les réponses restées en tête comptent comme non lues : elles le sont, et
    // le bouton flottant est le seul endroit qui le dise panneau fermé.
    const unreadCount = visibleUnread.length + finishedHere.length
    if (unreadCount) {
      parts.push(`${unreadCount} réponse${unreadCount > 1 ? 's non lues' : ' non lue'}`)
    }
    return parts.length ? `Ouvrir l'assistant builder — ${parts.join(', ')}` : "Ouvrir l'assistant builder"
  }, [backgroundLabel, visibleUnread.length, finishedHere.length])

  // Re-read on every opening rather than on every message: the list only has to
  // be right at the moment it is looked at.
  const toggleHistory = useCallback(() => {
    // The fetch stays OUT of the state updater: React may run an updater twice.
    if (!showHistory) {
      loadBuilderHistory().then(setHistoryEntries).catch(() => { /* private mode */ })
    }
    setShowHistory((v) => !v)
  }, [showHistory])

  // « Voir » OUVRE, il ne bascule pas : cliquer sur un signalement pour faire
  // disparaître ce qu'il désigne se lit comme une panne.
  const openHistory = useCallback(() => {
    if (!showHistory) toggleHistory()
  }, [showHistory, toggleHistory])

  // The whole entry goes back to the hook — `sessionId` included, which is what
  // makes the agent continue the conversation instead of starting over.
  const resumeFromHistory = useCallback((entry) => {
    // Écrire AVANT de basculer : `resumeConversation` remplace le transcript et
    // l'id, et l'effet de persistance qui suit écrirait alors la conversation
    // qu'on vient d'ouvrir, pas celle qu'on quitte — dont le point de reprise
    // serait perdu. Même ordre que `detachStream`, pour la même raison.
    flushConversation()
    // Le bloc ciblé revient AVEC la conversation. Il était jusqu'ici dépinglé
    // d'office : l'assistant repartait alors sans contexte — et, la demande
    // suivante ne désignant plus rien, créait un bloc au lieu de modifier celui
    // dont on parlait depuis le début. Une entrée qui n'existe que côté serveur
    // (ou d'avant cet enregistrement) n'en a pas : on dépingle, comme avant.
    setTarget(entry.target ?? null)
    setUsage(null)
    // Une entrée venue du SERVEUR peut n'avoir aucun message local : le tour a été
    // lancé depuis un autre onglet, ou sa réponse est arrivée après la fermeture du
    // navigateur. On sème alors la question, que seul le serveur a gardée, pour que
    // la réponse qui va être rejouée ait un contexte à l'écran.
    const seeded = entry.messages?.length
      ? entry.messages
      : (entry.run?.title ? [{ id: `u-${entry.id}`, role: 'user', text: entry.run.title }] : [])

    resumeConversation({
      conversationId: entry.id,
      sessionId: entry.sessionId,
      messages: seeded,
      // Vient de l'entrée locale, ou du serveur pour un tour que ce navigateur
      // n'a jamais vu (cf. mergeRuns).
      origin: entry.origin,
    })
    setShowHistory(false)

    // On se rebranche si le tour tourne encore, ou si ce navigateur n'a AUCUN
    // transcript à afficher — la réponse n'existe alors que côté serveur.
    // Volontairement pas conditionné à « non lue » : ce drapeau ne sert qu'au
    // badge, et s'en servir ici rendait une réponse déjà consultée une fois
    // définitivement inaccessible depuis un navigateur vierge.
    if (entry.run && (isLive(entry.run) || !entry.messages?.length)) {
      resumeRun({
        conversationId: entry.id,
        // Le curseur local évite de rejouer trois cents trames pour en lire
        // trois ; `run.from` (le tour entier) est le repli qui marche toujours.
        lastId: entry.pending?.lastId ?? entry.run.from,
        // Le contexte de coût du tour d'origine, pas un `builder_catalogue` en
        // dur : un tour de tableau de bord rejoint atterrissait dans la mauvaise
        // ligne du tableau de bord des coûts.
        context: entry.pending?.context ?? 'builder_catalogue',
      })
    }
  }, [flushConversation, resumeConversation, resumeRun])

  // Ouvrir une réponse restée en tête. Deux effets, et le premier ne suffit pas :
  // `resumeFromHistory` ne se rebranche (donc ne marque livré) que si le tour
  // court encore ou si ce navigateur n'a aucun transcript — un tour terminé
  // qu'on a vu s'écrire ici remplit les deux fois la condition à faux.
  // `readHere` fait tomber la ligne TOUT DE SUITE : le serveur, lui, rend
  // `delivered: null` tant que son registre tient le tour, et la ligne serait
  // restée encore dix minutes après avoir été lue.
  const readFinished = useCallback((entry) => {
    setReadHere(prev => [...new Set([...prev, entry.id])])
    markRunsRead([entry.id])
    resumeFromHistory(entry)
  }, [resumeFromHistory])

  /**
   * Retirer une réponse de la liste sans l'ouvrir.
   *
   * Le pendant du « Masquer » du bandeau du bas, et la même portée : cette
   * page-ci. Le drapeau du serveur n'est PAS abaissé — on n'a pas lu la réponse,
   * seulement rangé la ligne —, donc elle reste annonçable ailleurs, et revient
   * dans « pendant votre absence » au prochain chargement. C'est « Lire » qui
   * marque une réponse comme vue.
   */
  const dismissFinished = useCallback((conversationId) => {
    setReadHere(prev => [...new Set([...prev, conversationId])])
  }, [])

  /** Ranger d'un geste tout ce qui a fini — ce qui travaille encore reste. */
  const dismissAllFinished = useCallback(() => {
    setReadHere(prev => [...new Set([...prev, ...finishedHere.map(run => run.conversationId)])])
  }, [finishedHere])

  // Rouvrir le panneau, c'est revenir regarder. Si le tour de la conversation
  // affichée tourne encore, on s'y rebranche : la fermeture a lâché le flux
  // (pour rendre le worker), et sans ceci la réouverture laisserait un
  // transcript figé pendant que le run continue — le symptôme même qu'on répare.
  useEffect(() => {
    if (!open || isStreaming) return
    const run = serverRuns.find(r => r.conversationId === conversationId)
    if (!isLive(run)) return
    const cursor = detachedCursorRef.current
    resumeRun({
      // Le curseur mémorisé n'est utilisable que s'il appartient à CETTE
      // conversation : un `from` d'un autre run est traité `gone` par
      // /chat/attach, et l'éditeur lirait « la réponse n'est plus disponible »
      // sur un tour parfaitement vivant.
      conversationId,
      lastId: cursor?.conversationId === conversationId ? cursor.lastId : run.from,
      context: cursor?.conversationId === conversationId ? cursor.context : 'builder_catalogue',
    })
  }, [open, isStreaming, serverRuns, conversationId, resumeRun])

  // Une réponse d'arrière-plan vient de se terminer. Le sondage est la seule
  // source — `/chat/runs` rend un état, pas un flux — d'où la comparaison avec
  // le sondage précédent.
  const prevRunsRef = useRef([])
  // Les tours dont la fin est à signaler — pas le texte. Le texte s'en DÉDUIT,
  // et c'est ce qui fait taire le signalement dès que l'éditeur arrive sur la
  // conversation : rien à effacer, rien à réarmer, la ligne n'a simplement plus
  // de raison d'exister. Voir `backgroundNotice` juste dessous.
  const [noticeRuns, setNoticeRuns] = useState(null)
  useEffect(() => {
    const done = newlyFinished(prevRunsRef.current, serverRuns, attachedId)
    prevRunsRef.current = serverRuns
    if (done.length === 0) return

    // Ce qui garde sa ligne en tête n'a pas besoin d'être annoncé une seconde
    // fois en bas : la ligne le dit déjà, à l'endroit où l'éditeur la suivait.
    // Reste ici le cas résiduel d'un tour lancé d'ici mais regardé finir
    // AILLEURS (autre onglet) : le serveur le donne pour livré, aucune ligne ne
    // le retient, et sans ce texte sa fin passerait inaperçue sur cet écran.
    // Le rafraîchissement du canevas plus bas, lui, vaut pour TOUS les tours
    // finis : ce qu'ils ont écrit est en base, annoncé ou pas.
    const announced = done.filter(run => !finishedHere.some(f => f.conversationId === run.conversationId))
    if (announced.length > 0) {
      // On garde les TOURS, pas leur texte : c'est le rendu qui écarte celui que
      // l'éditeur a sous les yeux (cf. `backgroundNotice`).
      setNoticeRuns(announced)

      // Annoncée UNE fois, ici. Le bandeau « pendant votre absence » lit la même
      // liste (`stored && delivered === false`) et la reproposerait au sondage
      // suivant : deux textes pour un seul fait, et l'éditeur croirait à deux
      // réponses. On l'éteint pour CETTE session seulement — le drapeau serveur
      // reste à false, donc un autre onglet reste prévenu.
      //
      // Y COMPRIS celle qu'on regarde et qui ne sera pas annoncée : ne pas la
      // signaler parce qu'elle est à l'écran serait vain si le bandeau du bas
      // la rattrapait deux secondes plus tard.
      setDismissedUnread(prev => [...new Set([...prev, ...announced.map(run => run.conversationId)])])
    }

    // Ces tours-là ont fini SANS que personne ne tienne leur flux : le hook ne
    // peut donc pas les annoncer, et c'est ce sondage qui en tient lieu. Une
    // vraie invalidation, une par conversation — pas un simple rafraîchissement :
    // c'est elle qui dépense la place réservée par le « + » et qui pose le bloc.
    // Sans cela, un tour lâché en cours de route (le geste NORMAL dès qu'on
    // enchaîne deux demandes) ne posait jamais rien.
    //
    // Une seule conversation par annonce : c'est ce qui empêche un tour de
    // dépenser la place d'un autre (cf. blockPlacement).
    for (const run of done) {
      if (announcedRef.current.has(run.conversationId)) continue
      // `background` : cette fin-là est apprise par SONDAGE, pas par un flux
      // qu'on regardait. Le canevas doit servir la place réservée de cette
      // conversation — et RIEN d'autre : un tour d'un autre onglet, ou d'une
      // page qu'on a quittée, déposerait sinon son bloc en bas de la page
      // ouverte, que personne n'a demandé.
      fireInvalidation({ types: [], conversationId: run.conversationId, background: true })
    }
    if (host) onDashboardInvalidate?.()
  }, [serverRuns, attachedId, finishedHere, fireInvalidation, host, onDashboardInvalidate])

  /**
   * Le texte du signalement — DÉDUIT des tours à annoncer, jamais figé.
   *
   * La conversation ouverte sous les yeux de l'éditeur en est écartée : sa
   * réponse est déjà à l'écran, l'annoncer « prête » envoie chercher ailleurs ce
   * qu'on est en train de lire. `attachedId` ne suffisait pas à l'écarter — il
   * retombe à `null` quand le flux se termine, c'est-à-dire à l'instant précis
   * où le sondage constate la fin ; et un tour trop court pour être vu tourner
   * par un sondage n'entre pas non plus dans `finishedHere`.
   *
   * Déduire plutôt que retenir un texte règle aussi le cas d'après : l'éditeur
   * clique « Voir », ouvre la conversation annoncée, et le bandeau s'éteint de
   * lui-même — sans effet de nettoyage à écrire, ni risque d'en garder un
   * périmé. Panneau fermé, rien n'est « sous les yeux » : on annonce tout.
   */
  const backgroundNotice = useMemo(() => {
    const runs = (noticeRuns ?? []).filter(run => !(open && run.conversationId === conversationId))
    if (runs.length === 0) return null
    return runs.length === 1
      ? `La réponse de « ${runs[0].title || 'la conversation'} » est prête.`
      : `${runs.length} réponses d'arrière-plan sont prêtes.`
  }, [noticeRuns, open, conversationId])

  // `?chat=<conversationId>` : le lien d'adresse d'une ligne d'historique. Il
  // emmène sur la page d'où la conversation est partie ET demande à l'assistant
  // de s'y rouvrir dessus — sinon on arriverait au bon endroit en ayant perdu
  // ce qu'on venait y chercher.
  //
  // Lu au RENDU, pas dans l'effet : l'application réécrit son URL (elle en
  // retire `?edit=1`), et un paramètre lu trop tard aurait déjà disparu.
  const requestedChat = useMemo(() => (
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get(CHAT_PARAM)
  ), [])

  useEffect(() => {
    if (!requestedChat || loadedRef.current) return
    // Arme le même verrou que la reprise « dernière conversation » : sans ça,
    // l'ouverture qui suit irait recharger la plus récente par-dessus celle-ci.
    loadedRef.current = true
    setOpen(true)
    // Les deux sources, parce qu'une conversation désignée par un lien peut très
    // bien n'exister que côté serveur : lien envoyé à un collègue, navigateur
    // neuf, ou réponse arrivée après la fermeture de celui-ci.
    Promise.all([loadBuilderHistory().catch(() => []), fetchChatRuns()]).then(([entries, runs]) => {
      setHistoryEntries(entries)
      setServerRuns(runs)
      const entry = mergeRuns(entries, runs).find(e => e.id === requestedChat)
      // Volontairement pas de repli sur la dernière conversation : ouvrir autre
      // chose que ce qui était demandé se lirait comme un bug.
      if (entry) resumeFromHistory(entry)
    })
  }, [requestedChat, resumeFromHistory])

  // Deleting the conversation currently on screen would leave the panel showing
  // a transcript that no longer exists anywhere — clear it too.
  const removeFromHistory = useCallback(async (entry) => {
    setHistoryEntries((prev) => prev.filter((e) => e.id !== entry.id))
    if (entry.id === conversationId) resetAll()
    await deleteBuilderConversation(entry.id)
  }, [conversationId, resetAll])

  // Ce que dit la puce. En « création », il n'y a AUCUN bloc ciblé et aucun
  // écran de rattachement : le seul contexte transmis est le site (cf.
  // buildCreateContext) — la puce ne doit donc promettre rien d'autre.
  const targetLabel = target?.intent === 'create' ? 'Nouveau bloc' : target?.name

  // Les répertoires RÉELLEMENT ouverts, tels que le service les déclare. Écrire
  // la liste en dur ici la ferait mentir dès qu'on change une variable
  // d'environnement — et c'est une promesse faite à l'éditeur avant qu'il coche.
  const fileWriteScope = useMemo(() => {
    const roots = health?.fileWrite?.roots
    return Array.isArray(roots) && roots.length ? roots.join(', ') : null
  }, [health])
  const targetScope = target?.intent === 'create'
    ? (target.site?.name || null)
    : (target?.page?.slug
      ? `${target.page.kind === 'page' ? 'page' : 'tableau de bord'} ${target.page.slug}`
      : null)

  // Même contrat que dans l'historique : la page de départ, avec de quoi y
  // rouvrir l'assistant sur cette conversation. `null` si l'adresse n'est pas un
  // chemin interne — on l'affiche alors en texte.
  const originLink = useMemo(() => originHref(origin, conversationId), [origin, conversationId])

  // Celles du brouillon qui s'ouvrent en grand. Un id qui n'y figure plus (pièce
  // jointe retirée, message envoyé) referme la visionneuse de lui-même.
  const openableDraft = useMemo(() => openableAttachments(attachments), [attachments])

  const warning = useMemo(() => usageWarning(usage), [usage])
  // Une création rattache la déf au site : c'est une capacité PLUS RÉCENTE que
  // le contexte de bloc, et une image non reconstruite la perdrait en silence —
  // le bloc créé n'apparaîtrait dans aucun catalogue.
  const staleWarning = useMemo(() => {
    if (!target) return null
    // Une seule capacité nommée par intention — la PLUS RÉCENTE dont ce chip
    // dépend : elle implique les précédentes, et deux avertissements pour le
    // même conteneur périmé ne diraient pas deux choses.
    const feature = target.intent === 'create' && target.site?.id ? 'blockCreateSite' : 'pinnedBlockTurn'
    return staleSidecarWarning(health, feature)
  }, [health, target])

  const addFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || [])
    if (!files.length) return
    setAttachError(null)
    for (const file of files) {
      try {
        const att = await readFileAsAttachment(file)
        setAttachments(prev => [...prev, att])
      }
      catch (err) {
        setAttachError(err.message ?? 'Fichier ignoré')
      }
    }
  }, [])

  const removeAttachment = useCallback((id) => {
    setAttachments(prev => prev.filter(a => a.id !== id))
  }, [])

  const onPickFiles = useCallback((e) => {
    addFiles(e.target.files)
    e.target.value = '' // allow re-picking the same file
  }, [addFiles])

  const onPaste = useCallback((e) => {
    const files = Array.from(e.clipboardData?.files || []).filter(isAccepted)
    if (files.length) {
      e.preventDefault()
      addFiles(files)
    }
  }, [addFiles])

  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files')

  const onDragEnter = useCallback((e) => {
    if (!hasFiles(e)) return
    e.preventDefault()
    dragDepth.current += 1
    setIsDragging(true)
  }, [])

  const onDragOver = useCallback((e) => {
    if (hasFiles(e)) e.preventDefault()
  }, [])

  const onDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setIsDragging(false)
  }, [])

  const onDrop = useCallback((e) => {
    if (!hasFiles(e)) return
    e.preventDefault()
    dragDepth.current = 0
    setIsDragging(false)
    addFiles(e.dataTransfer.files)
  }, [addFiles])

  const submit = useCallback((e) => {
    e?.preventDefault?.()
    if (isStreaming) return
    if (!draft.trim() && attachments.length === 0) return
    // La demande part : c'est ICI, et nulle part ailleurs, que la place réservée
    // par un « + » trouve le tour qui la remplira. Armée au clic, elle n'avait
    // pas encore de conversation — le tour n'existait pas. Sans cette adoption,
    // le canevas ne saurait plus à quel tour rattacher son carré d'attente, ni
    // quel bloc poser où à la fin (cf. blockPlacement).
    claimPendingPlacements(conversationId)
    // `allowFileWrite` n'autorise RIEN par lui-même : le proxy Drupal le retire
    // du corps et repose la question à la session. C'est une intention, pas un
    // droit — cf. BuilderChatProxyController::stream().
    send(draft, attachments, target, { allowFileWrite: allowFileWrite && canOfferFileWrite })
    // Le tour est annoncé au canevas SANS attendre le sondage : c'est ce qui
    // permet de quitter la conversation dans la foulée sans éteindre le voile du
    // bloc qu'elle réécrit (cf. `pendingLocal`).
    if (target) setPendingLocal(prev => [...prev, { conversationId, target, at: Date.now() }])
    setDraft('')
    setAttachments([])
    setAttachError(null)
  }, [draft, attachments, isStreaming, send, target, conversationId, allowFileWrite, canOfferFileWrite])

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }, [submit])

  // --- Poignée de largeur -----------------------------------------------------
  // Événements POINTEUR et non souris : la même poignée sert au doigt et au
  // stylet, et la capture garantit qu'un glissement rapide qui sort du panneau
  // (donc de la colonne qui rétrécit sous le curseur) continue d'être suivi.
  const dragRef = useRef(null)

  const onResizeDown = useCallback((e) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture?.(e.pointerId)
    dragRef.current = { startX: e.clientX, startWidth: width }
    document.body.style.cursor = 'ew-resize'
    // Sans ça, le glissement sélectionne le transcript sous le curseur.
    document.body.style.userSelect = 'none'
  }, [width])

  const onResizeMove = useCallback((e) => {
    const drag = dragRef.current
    if (!drag) return
    // Le panneau est ancré à DROITE : aller vers la gauche l'élargit.
    setWidth(clampWidth(drag.startWidth + (drag.startX - e.clientX)))
  }, [])

  const onResizeUp = useCallback((e) => {
    if (!dragRef.current) return
    dragRef.current = null
    e.currentTarget.releasePointerCapture?.(e.pointerId)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [])

  const onResizeKeyDown = useCallback((e) => {
    const step = e.shiftKey ? RESIZE_STEP * 4 : RESIZE_STEP
    if (e.key === 'ArrowLeft') setWidth(w => clampWidth(w + step))
    else if (e.key === 'ArrowRight') setWidth(w => clampWidth(w - step))
    else if (e.key === 'Home') setWidth(clampWidth(PANEL_MAX_WIDTH))
    else if (e.key === 'End') setWidth(clampWidth(PANEL_MIN_WIDTH))
    else return
    e.preventDefault()
  }, [])

  const resetWidth = useCallback(() => setWidth(clampWidth(PANEL_DEFAULT_WIDTH)), [])

  if (!open) {
    // La POSITION appartient à la barre du coin, plus au bouton : plusieurs
    // actions flottantes peuvent coexister à l'écran sans se recouvrir.
    return (
      <CornerDockSlot
        order={DOCK_ORDER.builderChat}
        // La recette `.dock-slot` met l'emplacement à 70 % d'opacité au repos.
        // Un bouton qui porte un compteur vivant ne doit pas être à moitié
        // effacé : le contraste de la pastille se mesure sur le rendu.
        className={dockCount > 0 ? 'dock-slot-attention' : ''}
      >
        <BackgroundNotice text={backgroundNotice} />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-teal-600 text-white shadow-lg hover:bg-teal-700 transition-colors"
          aria-label={dockLabel}
        >
          <Sparkles size={16} aria-hidden="true" />
          <span className="text-sm font-medium">Assistant</span>
          {/* `aria-hidden` : le compte est DÉJÀ dans le nom accessible du bouton.
              Sans ça un lecteur d'écran lit « … 2 tours en cours, 2 ». En ligne
              dans la rangée flex et non en absolu : une pastille absolue
              sortirait du `rounded-full` et se ferait rogner par le portail. */}
          {dockCount > 0 && (
            <span className="badge badge-sm badge-warning" aria-hidden="true">{dockCount}</span>
          )}
        </button>
      </CornerDockSlot>
    )
  }

  return (
    <div
      // `fixed` alors que la place est réservée par ailleurs : la colonne doit
      // tenir toute la hauteur du viewport et ne pas défiler avec la page — c'est
      // `#orbit_front_app` qui se rétrécit d'exactement cette largeur, si bien
      // que les deux coïncident sans que rien ne soit recouvert.
      style={{ width }}
      className="fixed top-0 right-0 bottom-0 z-[200] max-w-[100vw] bg-white border-l border-gray-200 shadow-xl flex flex-col"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* La poignée. `separator` focalisable plutôt qu'un `div aria-hidden` :
          régler la largeur d'une colonne est une action, et une action réservée
          à la souris n'existe pas pour qui navigue au clavier (RGAA 12.x /
          WCAG 2.1.1). Double-clic = retour à la largeur par défaut. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Largeur de l’assistant"
        aria-valuenow={width}
        aria-valuemin={PANEL_MIN_WIDTH}
        aria-valuemax={clampWidth(PANEL_MAX_WIDTH)}
        tabIndex={0}
        title="Glisser pour redimensionner — double-clic pour la largeur par défaut"
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        onPointerCancel={onResizeUp}
        onKeyDown={onResizeKeyDown}
        onDoubleClick={resetWidth}
        className="group absolute left-0 top-0 bottom-0 w-2 -ml-1 z-10 cursor-ew-resize flex items-center justify-center touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
      >
        <span className="w-0.5 h-10 rounded-full bg-gray-300 group-hover:bg-teal-500 group-focus-visible:bg-teal-600 transition-colors" />
      </div>

      {isDragging && (
        <div className="absolute inset-0 z-10 m-2 rounded-xl border-2 border-dashed border-teal-400 bg-teal-50/90 flex flex-col items-center justify-center gap-2 pointer-events-none">
          <Paperclip size={28} className="text-teal-600" />
          <div className="text-sm font-medium text-teal-700">Déposer une image ou un PDF</div>
          <div className="text-[11px] text-teal-600/80">L'assistant l'analysera pour ce message</div>
        </div>
      )}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
        <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center">
          <Sparkles size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Assistant builder</div>
          <div className="text-[10px] text-gray-500">
            {/* Le modèle vient du SERVICE (variable d'environnement `MODEL`),
                pas d'un nom écrit ici : il a menti tant qu'il était en dur.
                Tant que la santé n'a pas répondu, on ne nomme rien — mieux vaut
                ne rien dire qu'annoncer le mauvais modèle. */}
            Catalogue de blocs{modelLabel(health?.model) ? ` · ${modelLabel(health.model)}` : ''}
            {sessionHeadline && (
              <span className="ml-1.5 text-gray-400 cursor-help" title={sessionTooltip}>
                · {sessionHeadline}
              </span>
            )}
          </div>
          {/* D'où la conversation est PARTIE, pas où l'on se trouve : une
              conversation rouverte depuis l'historique — ou reprise d'un autre
              onglet — se serait sinon perdue de vue. */}
          {origin && (
            <div className="text-[10px] text-gray-400 truncate">
              Depuis{' '}
              {originLink ? (
                <a
                  href={originLink}
                  title={`Aller sur ${origin} avec cette conversation ouverte`}
                  className="underline underline-offset-2 hover:text-gray-600"
                >
                  {origin}
                </a>
              ) : origin}
            </div>
          )}
        </div>
        {/* La pastille chiffrée qui vivait ici renvoyait vers l'historique pour
            apprendre CE qui tournait. Le bandeau juste dessous le NOMME, sur la
            même condition : deux annonces du même fait dans le même en-tête, dont
            la moins précise. Le compte reste sur le bouton flottant, seul endroit
            où le bandeau n'est pas visible (panneau fermé). */}
        <button
          type="button"
          onClick={toggleHistory}
          className={`p-1.5 rounded hover:bg-gray-100 ${showHistory ? 'text-teal-700 bg-teal-50' : 'text-gray-400 hover:text-gray-700'}`}
          aria-label="Historique des conversations"
          title="Historique des conversations"
          aria-expanded={showHistory}
          aria-controls={HISTORY_PANEL_ID}
        >
          <History size={14} />
        </button>
        <button
          type="button"
          onClick={resetAll}
          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
          aria-label="Nouvelle conversation"
          title="Nouvelle conversation"
        >
          <RotateCcw size={14} />
        </button>
        <button
          type="button"
          onClick={closePanel}
          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
          aria-label="Fermer"
        >
          <X size={16} />
        </button>
      </div>

      {/* Ce qui travaille AILLEURS que sous les yeux de l'éditeur, nommé plutôt
          que compté. Une pastille chiffrée dit qu'il se passe quelque chose ;
          elle ne dit pas QUOI, et il fallait ouvrir l'historique pour le
          savoir — alors même que c'est en épinglant un second bloc qu'on crée
          la situation. Toujours visible, transcript ou historique. */}
      {dockedRuns.length > 0 && (
        <div className="border-b border-amber-200 bg-amber-50">
        {/* Le geste global vit HORS de la région vivante ci-dessous : dedans, il
            serait relu à voix haute à chaque tour qui démarre ou s'achève. Il
            n'apparaît que s'il y a quelque chose à ranger — ce qui travaille
            encore n'est jamais retiré. */}
        {finishedHere.length > 0 && (
          <div className="flex items-center justify-end px-3 pt-1.5">
            <button
              type="button"
              onClick={dismissAllFinished}
              aria-label={`Masquer ${finishedHere.length === 1 ? 'la réponse prête' : `les ${finishedHere.length} réponses prêtes`}`}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
            >
              <X size={11} aria-hidden="true" />
              Tout masquer
            </button>
          </div>
        )}
        <ul
          // La SEULE région vivante des tours en cours : les voiles du canevas
          // ont perdu la leur, sinon deux tours de front faisaient lire trois
          // fois la même phrase.
          role="status"
          aria-label="Tours en arrière-plan"
          className="divide-y divide-amber-100"
        >
          {dockedRuns.map((run) => {
            const label = run.target?.name || run.title || 'Conversation'
            const entry = mergedHistory.find(e => e.id === run.conversationId)
            // Un tour ABOUTI garde sa ligne ici jusqu'à ce qu'on l'ouvre : il ne
            // saute pas dans le bandeau du bas au moment même où il donne son
            // résultat (cf. finishedHereRuns).
            const live = isLive(run)
            return (
              <li key={run.conversationId} className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-amber-800">
                {live ? (
                  <Loader2
                    size={11}
                    className={`flex-shrink-0 ${run.status === 'running' ? 'animate-spin motion-reduce:animate-none' : 'opacity-50'}`}
                    aria-hidden="true"
                  />
                ) : (
                  <Inbox size={11} className="flex-shrink-0" aria-hidden="true" />
                )}
                <span className="flex-1 min-w-0 truncate" title={label}>
                  {label}
                </span>
                <span className="flex-shrink-0 text-amber-700">
                  {!live
                    ? 'réponse prête'
                    : run.status === 'queued'
                      ? (run.queuePosition ? `en attente (${run.queuePosition}e)` : 'en attente')
                      : 'en cours'}
                </span>
                {/* Rejoindre le tour, pas seulement le constater : sans ça, la
                    seule façon d'y revenir passait par l'historique. */}
                {entry && (
                  <button
                    type="button"
                    onClick={() => (live ? resumeFromHistory(entry) : readFinished(entry))}
                    // Plusieurs lignes portent le même intitulé visible : sans
                    // nom accessible propre, la liste des liens d'un lecteur
                    // d'écran donne « Suivre, Suivre, Suivre » (WCAG 2.4.4).
                    aria-label={`${live ? 'Suivre' : 'Lire la réponse de'} « ${label} »`}
                    className="flex-shrink-0 underline underline-offset-2 hover:no-underline font-medium"
                  >
                    {live ? 'Suivre' : 'Lire'}
                  </button>
                )}
                {/* Ranger la ligne sans l'ouvrir. Seulement sur ce qui a FINI :
                    un tour au travail se rejoint ou s'arrête (le ■ du
                    composeur), il ne se masque pas — la ligne est le seul
                    endroit qui dise qu'il tourne. */}
                {!live && (
                  <button
                    type="button"
                    onClick={() => dismissFinished(run.conversationId)}
                    // Nommée, sinon un lecteur d'écran annonce « Masquer,
                    // Masquer, Masquer » sur une liste de trois réponses.
                    aria-label={`Masquer la réponse de « ${label} »`}
                    className="flex-shrink-0 rounded p-0.5 text-amber-700 hover:bg-amber-100 hover:text-amber-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
                  >
                    <X size={12} aria-hidden="true" />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
        </div>
      )}

      {showHistory ? (
        <BuilderChatHistory
          id={HISTORY_PANEL_ID}
          entries={mergedHistory}
          currentId={conversationId}
          onResume={resumeFromHistory}
          onDelete={removeFromHistory}
        />
      ) : (
        <MessageList messages={messages} isStreaming={isStreaming} />
      )}

      {/* Une réponse est arrivée pendant que l'éditeur n'était pas là : le run a
          continué sans son navigateur et s'est écrit sur disque en se terminant.
          On le DIT plutôt que de l'afficher d'office — la conversation courante
          n'est pas forcément celle qui a fini, et écraser ce qu'il regarde serait
          pire que de l'en informer. */}
      {/* Le signalement d'un tour d'arrière-plan qui vient de finir. La région
          vivante est `BackgroundNotice` ; cette ligne-ci est sa contrepartie
          VISIBLE et ne porte donc PAS de `role=status` — deux régions vivantes
          qui disent la même chose la font lire deux fois. */}
      <BackgroundNotice text={backgroundNotice} />
      {backgroundNotice && !showHistory && (
        <div className="px-3 py-2 text-[11px] text-amber-800 bg-amber-50 border-t border-amber-200 flex items-center gap-1.5">
          <Inbox size={12} className="flex-shrink-0" aria-hidden="true" />
          <span className="flex-1">{backgroundNotice}</span>
          <button
            type="button"
            onClick={openHistory}
            className="underline underline-offset-2 hover:no-underline font-medium"
          >
            Voir
          </button>
          <button
            type="button"
            onClick={() => setNoticeRuns(null)}
            className="flex-shrink-0 p-0.5 rounded text-amber-700 hover:bg-amber-100 hover:text-amber-900"
            aria-label="Masquer ce signalement"
            title="Masquer ce signalement"
          >
            <X size={12} aria-hidden="true" />
          </button>
        </div>
      )}

      {visibleUnread.length > 0 && !showHistory && (
        <div
          role="status"
          className="px-3 py-2 text-[11px] text-amber-800 bg-amber-50 border-t border-amber-200 flex flex-col gap-1"
        >
          <div className="flex items-start gap-1.5">
            <Inbox size={12} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span className="flex-1">
              {visibleUnread.length === 1
                ? 'Une réponse est arrivée pendant votre absence.'
                : `${visibleUnread.length} réponses sont arrivées pendant votre absence.`}
            </span>
            {/* Masquer, pas marquer comme lu : le message disparaît de CET écran
                et la réponse reste signalée non lue partout ailleurs. */}
            <button
              type="button"
              onClick={dismissUnread}
              className="flex-shrink-0 p-0.5 rounded text-amber-700 hover:bg-amber-100 hover:text-amber-900"
              aria-label="Masquer cette annonce"
              title="Masquer cette annonce"
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>
          <div className="flex items-center gap-3 pl-4">
            <button
              type="button"
              onClick={openHistory}
              className="underline underline-offset-2 hover:no-underline font-medium"
            >
              Voir
            </button>
            <button
              type="button"
              onClick={markUnreadRead}
              className="underline underline-offset-2 hover:no-underline font-medium"
            >
              {visibleUnread.length === 1 ? 'Marquer comme lue' : 'Tout marquer comme lu'}
            </button>
          </div>
        </div>
      )}

      {/* La coupure n'arrête PAS le tour : il continue côté sidecar et on s'y
          rebranche. On le dit, sans alarmer — d'où le ton d'un avertissement
          passager et non d'une erreur. `role=status` : le changement n'est pas
          sollicité par l'éditeur, il doit être annoncé. */}
      {/* Le tour attend son tour : d'autres éditeurs occupent les places. Un
          message d'attente, pas une erreur — le travail n'est pas perdu, il n'a
          pas encore commencé. */}
      {typeof queuePosition === 'number' && (
        <div
          role="status"
          className="px-3 py-2 text-[11px] text-gray-600 bg-gray-50 border-t border-gray-200 flex items-center gap-1.5"
        >
          <Loader2 size={12} className="animate-spin flex-shrink-0" aria-hidden="true" />
          {queuePosition === 1
            ? 'En attente d’une place — vous êtes le prochain.'
            : `En attente d’une place — ${queuePosition}e de la file.`}
        </div>
      )}

      {isReconnecting && (
        <div
          role="status"
          className="px-3 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-200 flex items-center gap-1.5"
        >
          <Loader2 size={12} className="animate-spin flex-shrink-0" aria-hidden="true" />
          Connexion interrompue — reconnexion à la réponse en cours…
        </div>
      )}

      {error && (
        <div className="px-3 py-2 text-[11px] text-red-700 bg-red-50 border-t border-red-200 break-words">
          {error}
        </div>
      )}

      {attachError && (
        <div className="px-3 py-1.5 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-200 break-words">
          {attachError}
        </div>
      )}

      <form onSubmit={submit} className="border-t border-gray-200 p-2 flex flex-col gap-2">
        {target && (
          <div className="rounded-lg border border-teal-200 bg-teal-50/70 px-2 py-1.5 flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <Sparkles size={13} className="text-teal-600 flex-shrink-0" aria-hidden="true" />
              <span className="text-[11px] text-teal-800 min-w-0 truncate">
                {target.intent === 'create' ? 'À créer :' : 'Bloc ciblé :'}{' '}
                <strong className="font-semibold">{targetLabel}</strong>
                {targetScope && (
                  <span className="text-teal-800">{' '}· {targetScope}</span>
                )}
              </span>
              <button
                type="button"
                onClick={unpinTarget}
                className="ml-auto p-0.5 rounded text-teal-600 hover:text-teal-900 hover:bg-teal-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                aria-label={target.intent === 'create'
                  ? 'Annuler la création de bloc'
                  : `Ne plus cibler le bloc ${targetLabel}`}
                title={target.intent === 'create' ? 'Annuler la création' : 'Ne plus cibler ce bloc'}
              >
                <X size={12} />
              </button>
            </div>
            {/* The live region is rendered UNCONDITIONALLY: a screen reader only
                reliably announces changes inside a region that was already in the
                DOM, and this text arrives asynchronously (the usage fetch). */}
            <div id={CHIP_WARNING_ID} role="status" aria-label="Portée du bloc ciblé" className="flex flex-col gap-1 empty:hidden">
              {warning && (
                <span className="flex items-start gap-1.5 text-[10px] text-amber-800">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-px" aria-hidden="true" />
                  <span>{warning}</span>
                </span>
              )}
              {/* The chip promises "this block is targeted". A running agent
                  service that predates that field would make it a lie — say so,
                  rather than let the editor wonder why the agent still asks
                  which block they mean. */}
              {staleWarning && (
                <span className="flex items-start gap-1.5 text-[10px] text-red-700">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-px" aria-hidden="true" />
                  <span>{staleWarning}</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Le DROIT est vérifié par Drupal à chaque tour ; cette case n'est que
            le CONSENTEMENT, et il ne vaut que pour la conversation en cours.
            Elle n'apparaît ni sans la permission, ni sur un déploiement où le
            service n'ouvre pas l'écriture (`fileWrite` absent de /health). */}
        {canOfferFileWrite && (
          <div className="rounded-lg border border-gray-200 px-2 py-1.5">
            {/* La mention est SŒUR du label, pas dedans : le nom accessible de la
                case se calcule sur tout le contenu du label, et un lecteur
                d'écran annoncerait sinon la phrase entière comme intitulé, puis
                la relirait comme description. */}
            <label className="flex items-center gap-2 text-[11px] text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox"
                checked={allowFileWrite}
                onChange={e => setAllowFileWrite(e.target.checked)}
                aria-describedby={FILE_WRITE_HINT_ID}
              />
              Autoriser l’écriture de fichiers
            </label>
            <span id={FILE_WRITE_HINT_ID} className="field-hint">
              {fileWriteScope
                ? `L’assistant pourra écrire dans ${fileWriteScope}. Décoché à chaque nouvelle conversation.`
                : 'Décoché à chaque nouvelle conversation.'}
            </span>
          </div>
        )}

        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachments.map(att => (
              <div
                key={att.id}
                className="relative flex items-center gap-1.5 pr-1 pl-1 py-1 rounded-lg border border-gray-200 bg-gray-50"
              >
                {att.kind === 'image' ? (
                  // Ouvrable AVANT l'envoi aussi : c'est là qu'on vérifie
                  // qu'on a joint la bonne capture, pas après.
                  <button
                    type="button"
                    onClick={() => setOpenAttachmentId(att.id)}
                    aria-label={`Ouvrir la pièce jointe ${att.name}`}
                    title={`Ouvrir ${att.name}`}
                    className="rounded overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                  >
                    <img src={att.thumbUrl} alt={att.name} className="w-9 h-9 object-cover" />
                  </button>
                ) : (
                  <div className="w-9 h-9 rounded bg-red-50 text-red-500 flex items-center justify-center">
                    <FileText size={16} />
                  </div>
                )}
                <span className="text-[11px] text-gray-600 max-w-[110px] truncate">{att.name}</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(att.id)}
                  className="ml-0.5 p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded"
                  aria-label={`Retirer ${att.name}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <AttachmentViewer
              items={openableDraft}
              openId={openAttachmentId}
              onOpen={setOpenAttachmentId}
              onClose={() => setOpenAttachmentId(null)}
            />
          </div>
        )}

        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            onChange={onPickFiles}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isStreaming}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-30"
            aria-label="Joindre une image ou un PDF"
            title="Joindre une image ou un PDF"
          >
            <Paperclip size={16} />
          </button>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            // A placeholder is not an accessible name. It matters more than
            // usual here: the focus lands in this field programmatically, so a
            // screen-reader user must hear WHICH block is now targeted.
            aria-label={!target
              ? "Demande à l'assistant builder"
              : target.intent === 'create'
                ? "Décris le bloc à créer à l'assistant"
                : `Demande à l'assistant pour le bloc ${targetLabel}`}
            aria-describedby={target && warning ? CHIP_WARNING_ID : undefined}
            placeholder={
              isStreaming
                ? 'Réponse en cours…'
                : !target
                  ? 'Liste les blocs, dépose une maquette…'
                  : target.intent === 'create'
                    ? 'Décris le bloc à créer…'
                    : 'Que faut-il changer sur ce bloc ?'
            }
            disabled={isStreaming}
            rows={3}
            className="flex-1 resize-none border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 disabled:bg-gray-50"
            // Trois lignes au repos, une douzaine au maximum. Une seule ligne
            // obligeait à décrire un bloc entier par une fente : on écrivait à
            // l'aveugle dès la deuxième phrase, et se relire avant d'envoyer
            // demandait de faire défiler.
            style={{ minHeight: '72px', maxHeight: '260px' }}
          />
          {isStreaming ? (
            <button
              type="button"
              onClick={stop}
              className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
              aria-label="Arrêter"
            >
              <Square size={16} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!draft.trim() && attachments.length === 0}
              className="p-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-30 disabled:hover:bg-teal-600"
              aria-label="Envoyer"
            >
              <ArrowUp size={16} />
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

export { REGISTRY_INVALIDATE_EVENT, REGISTRY_REFRESH_EVENT }
